import React, { useRef, useEffect, useState } from "react";

const fmt  = (n) => n == null ? "—" : `$${Number(n).toFixed(2)}`;
const fmtN = (n) => n == null ? "—" : Number(n).toLocaleString();
const pnlColor = (n) => (n >= 0 ? "#28a745" : "#dc3545");

function FlashCell({ value, children }) {
  const prev   = useRef(value);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (prev.current !== value) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      prev.current = value;
      return () => clearTimeout(t);
    }
  }, [value]);

  return (
    <td style={{ ...td, background: flash ? "rgba(255,243,128,0.6)" : "transparent", transition: "background 0.3s" }}>
      {children}
    </td>
  );
}

export default function PositionsPanel({ frame }) {
  if (!frame) return <div style={empty}>Waiting for data…</div>;

  const stock   = frame.positions?.stock   || {};
  const options = frame.positions?.options || [];

  const hasStock   = stock.qty > 0;
  const hasOptions = options.length > 0;

  if (!hasStock && !hasOptions) {
    return <div style={empty}>No positions yet</div>;
  }

  return (
    <div style={wrap}>
      {hasStock && (
        <div style={section}>
          <div style={sectionTitle}>Stock</div>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Qty</th>
                <th style={th}>Avg</th>
                <th style={th}>LTP</th>
                <th style={th}>P&L</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <FlashCell value={stock.qty}><span style={{ fontWeight: 600 }}>{fmtN(stock.qty)}</span></FlashCell>
                <FlashCell value={stock.avg_price}>{fmt(stock.avg_price)}</FlashCell>
                <FlashCell value={frame.stock_ltp}>{fmt(frame.stock_ltp)}</FlashCell>
                <FlashCell value={stock.unrealised}>
                  <span style={{ color: pnlColor(stock.unrealised), fontWeight: 600 }}>
                    {stock.unrealised >= 0 ? "+" : ""}{fmt(stock.unrealised)}
                  </span>
                </FlashCell>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {hasOptions && (
        <div style={section}>
          <div style={sectionTitle}>Options</div>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Strike</th>
                <th style={th}>Type</th>
                <th style={th}>Lots</th>
                <th style={th}>Avg</th>
                <th style={th}>LTP</th>
                <th style={th}>Δ</th>
                <th style={th}>P&L</th>
              </tr>
            </thead>
            <tbody>
              {options.map((opt, i) => (
                <tr key={i} style={{ borderTop: "1px solid #f5f5f5" }}>
                  <FlashCell value={opt.strike}>
                    <span style={{ fontWeight: 600, color: opt.type === "CE" ? "#3b82f6" : "#e95c5c" }}>
                      ${opt.strike}
                    </span>
                  </FlashCell>
                  <FlashCell value={opt.type}>
                    <span style={{ ...badge, background: opt.type === "CE" ? "#dbeafe" : "#ffe4e4", color: opt.type === "CE" ? "#1d4ed8" : "#dc2626" }}>
                      {opt.type}
                    </span>
                  </FlashCell>
                  <FlashCell value={opt.lots}>{opt.lots}</FlashCell>
                  <FlashCell value={opt.avg_price}>{fmt(opt.avg_price)}</FlashCell>
                  <FlashCell value={opt.ltp}>{fmt(opt.ltp)}</FlashCell>
                  <FlashCell value={opt.delta}><span style={{ color: "#6f42c1" }}>{Number(opt.delta || 0).toFixed(2)}</span></FlashCell>
                  <FlashCell value={opt.unrealised}>
                    <span style={{ color: pnlColor(opt.unrealised), fontWeight: 600 }}>
                      {opt.unrealised >= 0 ? "+" : ""}{fmt(opt.unrealised)}
                    </span>
                  </FlashCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const wrap         = { overflow: "auto" };
const section      = { marginBottom: 12 };
const sectionTitle = { fontSize: 11, fontWeight: 700, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 };
const table        = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const th           = { padding: "4px 6px", textAlign: "left", color: "#999", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: "1px solid #f0f0f0" };
const td           = { padding: "5px 6px", color: "#333" };
const badge        = { padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 };
const empty        = { color: "#bbb", fontSize: 12, textAlign: "center", padding: "20px 0" };
